import axios from "axios";
import { attachRequestId } from "@/lib/observability";

// A trailing slash on REACT_APP_BACKEND_URL produces "//api", which the backend
// treats as a different path and answers with 404 — every call then fails while
// the variable still looks correct in the dashboard. Strip them here rather than
// depend on how the value was typed.
let BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
while (BACKEND_URL.endsWith("/")) BACKEND_URL = BACKEND_URL.slice(0, -1);

export const API_BASE = `${BACKEND_URL}/api`;

// Listing photos are stored as backend-relative paths ("/api/partner-media/…").
// The frontend is a separate Railway service, so an <img> pointing at one of
// those asks the frontend host for it and gets the SPA's index.html back —
// a broken image with a 200 next to it. Give them the API's origin.
export const mediaUrl = (url) => {
  if (!url) return "";
  return /^(https?:|data:|blob:)/.test(url) ? url : `${BACKEND_URL}${url.startsWith("/") ? "" : "/"}${url}`;
};

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Attach JWT if available
api.interceptors.request.use((config) => {
  let token = null;
  try { token = localStorage.getItem("touki_token"); } catch { /* public/cookie requests can still proceed */ }
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (config.method === "post" && config.url === "/payments/checkout") {
    const guestToken = getGuestToken(config.data?.booking_id);
    if (guestToken) config.params = { ...config.params, token: guestToken };
  }
  return config;
});

// A failed call keeps the request id the API stamped on its response, so the
// toast that shows the failure can carry a reference support can look up.
api.interceptors.response.use((response) => response, (error) => Promise.reject(attachRequestId(error)));

// Guest bookings are readable only with the access token issued at booking time.
const GUEST_TOKENS_KEY = "touki_guest_tokens";

const readGuestTokens = () => {
  try { return JSON.parse(localStorage.getItem(GUEST_TOKENS_KEY)) || {}; } catch { return {}; }
};

export const rememberGuestToken = (booking) => {
  if (!booking?.id || !booking?.guest_access_token) return;
  try {
    localStorage.setItem(GUEST_TOKENS_KEY, JSON.stringify({
      ...readGuestTokens(), [booking.id]: booking.guest_access_token,
    }));
  } catch { /* storage unavailable — the booking is still reachable via Manage booking */ }
};

export const getGuestToken = (id) => readGuestTokens()[id];

export const fetchBooking = (id) => {
  const token = readGuestTokens()[id];
  return api.get(`/bookings/${id}`, token ? { params: { token } } : undefined);
};

export default api;
